const printsGrid = (settings = {}) => {
  return {
    settings,
    loading: false,
    error: '',
    items: [],

    init() {
      this.fetchItems();
    },

    async fetchItems() {
      this.loading = true;
      this.error = '';

      try {
        const reference = this.$el.dataset.metaobjectReference || this.settings.metaobject_reference || '';
        const typeCandidates = this.getTypeCandidates(reference);

        if (typeCandidates.length === 0) {
          throw new Error('Metaobject reference is required.');
        }

        const entries = await this.fetchMetaobjectEntries(typeCandidates);
        const allMapped = entries.map((entry) => this.mapItem(entry));
        const hasSaleItems = allMapped.some((item) => item.isOnSale);

        this.items = allMapped
          .sort((a, b) => this.getItemTimestamp(b) - this.getItemTimestamp(a))
          .filter((item) => {
            if (!item.title || item.hideOnPrintsPage) return false;
            return this.settings.sale_mode ? item.isOnSale : !item.isOnSale;
          });

        if (!this.settings.sale_mode && hasSaleItems) {
          const teaserTitle = this.settings.sale_teaser_title || '';
          const teaserLink = this.settings.sale_teaser_link || '';
          const teaserImageUrl = this.$el.dataset.saleTeaserImage || '';
          console.log('Teaser Image URL:', teaserImageUrl);
          console.log('Teaser Title:', teaserTitle);
          console.log('Teaser Link:', teaserLink);
          if (teaserTitle || teaserImageUrl) {
            this.items.push({
              id: '__sale-teaser__',
              updatedAt: '',
              title: teaserTitle,
              image: teaserImageUrl ? { url: teaserImageUrl, alt: teaserTitle, width: 320, height: 320 } : null,
              link: teaserLink,
              hideOnPrintsPage: false,
              isOnSale: false
            });
          }
        }
      } catch (error) {
        console.error('[PrintsGrid] Failed to fetch items:', error);
        this.error = 'Unable to load items at the moment.';
      } finally {
        this.loading = false;
      }
    },

    getTypeCandidates(reference) {
      const normalized = (reference || '').trim();
      if (!normalized) return [];

      const candidates = [normalized];

      if (normalized.includes('.')) {
        const fallback = normalized.split('.').pop().trim();
        if (fallback && !candidates.includes(fallback)) {
          candidates.push(fallback);
        }
      }

      return candidates;
    },

    mapItem(entry) {
      const title = this.getFieldValue(entry.fields, 'title') || '';
      const imageField = this.getField(entry.fields, 'circular_image');
      const collectionField = this.getField(entry.fields, 'collection_url');

      const image = imageField?.reference?.image
        ? {
            url: imageField.reference.image.url,
            alt: imageField.reference.image.altText || title,
            width: imageField.reference.image.width,
            height: imageField.reference.image.height
          }
        : null;

      const link = collectionField?.reference?.onlineStoreUrl || collectionField?.value || '';

      return {
        id: entry.id,
        updatedAt: entry.updatedAt || '',
        title,
        image,
        link,
        hideOnPrintsPage: this.getFieldValue(entry.fields, 'hide_on_prints_page') === 'true',
        isOnSale: this.getFieldValue(entry.fields, 'print_is_on_sale') === 'true'
      };
    },

    getItemTimestamp(item) {
      const timestamp = Date.parse(item?.updatedAt || '');
      return Number.isNaN(timestamp) ? 0 : timestamp;
    },

    getField(fields, key) {
      return fields?.find((item) => item.key === key) || null;
    },

    getFieldValue(fields, key) {
      return this.getField(fields, key)?.value || '';
    },

    async fetchMetaobjectEntries(typeCandidates) {
      const accessToken = window.themeSettings?.storefront_access_token;

      if (!accessToken) {
        throw new Error('Storefront access token not found in window.themeSettings');
      }

      const MAX_ITEMS = Number(this.settings.max_items) || 1000;
      const PAGE_SIZE = 250; // Storefront API's hard cap on `first` for this connection

      const query = `
        query GetPrintsGridEntries($type: String!, $first: Int!, $after: String) {
          metaobjects(type: $type, first: $first, after: $after) {
            edges {
              node {
                id
                updatedAt
                fields {
                  key
                  value
                  reference {
                    ... on MediaImage {
                      image {
                        url
                        altText
                        width
                        height
                      }
                    }
                    ... on Collection {
                      onlineStoreUrl
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;

      const fetchPage = async (type, after) => {
        const response = await fetch('/api/2023-07/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken
          },
          body: JSON.stringify({
            query,
            variables: { type, first: PAGE_SIZE, after: after || null }
          })
        });

        if (!response.ok) {
          throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        return data.data?.metaobjects;
      };

      let lastEntries = [];

      for (const type of typeCandidates) {
        let entries = [];
        let after = null;

        do {
          const page = await fetchPage(type, after);
          entries = entries.concat(page?.edges?.map((edge) => edge.node) || []);
          after = page?.pageInfo?.hasNextPage ? page.pageInfo.endCursor : null;
        } while (after && entries.length < MAX_ITEMS);

        lastEntries = entries;

        if (entries.length > 0) {
          return entries;
        }
      }

      return lastEntries;
    }
  };
};

export default {
  name: 'printsGrid',
  component: printsGrid
};
